import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface NoteTab {
  id: string;
  title: string;
}

interface NoteTabsContextType {
  tabs: NoteTab[];
  activeTabId: string | null;
  addTab: (id: string, title: string) => void;
  removeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  updateTabTitle: (id: string, title: string) => void;
}

const NoteTabsContext = createContext<NoteTabsContextType | undefined>(undefined);

const STORAGE_KEY = 'note-tabs';
const ACTIVE_TAB_KEY = 'active-note-tab';

export const NoteTabsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tabs, setTabs] = useState<NoteTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  // Load tabs from localStorage on mount
  useEffect(() => {
    const storedTabs = localStorage.getItem(STORAGE_KEY);
    const storedActiveTab = localStorage.getItem(ACTIVE_TAB_KEY);

    if (storedTabs) {
      try {
        setTabs(JSON.parse(storedTabs));
      } catch (e) {
        console.error('Failed to parse stored tabs:', e);
      }
    }

    if (storedActiveTab) {
      setActiveTabId(storedActiveTab);
    }
  }, []);

  // Persist tabs to localStorage
  useEffect(() => {
    if (tabs.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [tabs]);

  // Persist active tab to localStorage
  useEffect(() => {
    if (activeTabId) {
      localStorage.setItem(ACTIVE_TAB_KEY, activeTabId);
    } else {
      localStorage.removeItem(ACTIVE_TAB_KEY);
    }
  }, [activeTabId]);

  const addTab = useCallback((id: string, title: string) => {
    setTabs((prevTabs) => {
      // Check if tab already exists
      if (prevTabs.some(tab => tab.id === id)) {
        return prevTabs;
      }
      return [...prevTabs, { id, title }];
    });
  }, []);

  const removeTab = useCallback((id: string) => {
    setTabs((prevTabs) => {
      const newTabs = prevTabs.filter(tab => tab.id !== id);

      // If removing active tab, clear active state
      if (activeTabId === id) {
        setActiveTabId(null);
      }

      return newTabs;
    });
  }, [activeTabId]);

  const setActiveTab = useCallback((id: string) => {
    setActiveTabId(id);
  }, []);

  const updateTabTitle = useCallback((id: string, title: string) => {
    setTabs((prevTabs) =>
      prevTabs.map(tab =>
        tab.id === id ? { ...tab, title: title || 'Untitled' } : tab
      )
    );
  }, []);

  return (
    <NoteTabsContext.Provider
      value={{
        tabs,
        activeTabId,
        addTab,
        removeTab,
        setActiveTab,
        updateTabTitle,
      }}
    >
      {children}
    </NoteTabsContext.Provider>
  );
};

const emptyNoteTabs: NoteTabsContextType = {
  tabs: [],
  activeTabId: null,
  addTab: () => {},
  removeTab: () => {},
  setActiveTab: () => {},
  updateTabTitle: () => {},
};

export const useNoteTabs = (): NoteTabsContextType => {
  const context = useContext(NoteTabsContext);
  return context ?? emptyNoteTabs;
};
